// storage.js — per-patient writes, offline queue, real-time via firebase.js
import {
  isFirebaseConfigured,
  savePatient,
  removePatient,
  savePatientsBatch,
  saveShiftMeta,
  loadShiftMeta,
  loadPatients,
} from './firebase';

const PREFIX = 'handoff_';
const QUEUE_KEY = PREFIX + 'sync_queue';

// ── LOCAL CACHE ───────────────────────────────────────────────
const local = {
  get:    (key)        => { try { const r = localStorage.getItem(PREFIX + key); return r ? JSON.parse(r) : null; } catch { return null; } },
  set:    (key, value) => { try { localStorage.setItem(PREFIX + key, JSON.stringify(value)); } catch(e) { console.warn('localStorage write failed:', e); } },
  remove: (key)        => { try { localStorage.removeItem(PREFIX + key); } catch {} },
};

// ── OFFLINE QUEUE ─────────────────────────────────────────────
// Each entry: { type: 'save'|'delete', patientId, patient?, teams?, ts }
const queue = {
  get:   () => { try { const r = localStorage.getItem(QUEUE_KEY); return r ? JSON.parse(r) : []; } catch { return []; } },
  push:  (item) => {
    const q = queue.get();
    // Deduplicate: if same patientId already queued as 'save', replace it
    const filtered = item.type === 'save'
      ? q.filter(e => !(e.type === 'save' && e.patientId === item.patientId))
      : q;
    filtered.push({ ...item, ts: Date.now() });
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(filtered.slice(-100))); } catch {}
  },
  clear: () => { try { localStorage.removeItem(QUEUE_KEY); } catch {} },
  remove: (patientId) => {
    const q = queue.get().filter(e => e.patientId !== patientId);
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch {}
  },
};

let activeShiftCode = null;
let cachedTeams = null; // eslint-disable-line no-unused-vars
let syncInProgress = false;

export const setActiveShift = (code, teams) => {
  activeShiftCode = code;
  cachedTeams = teams;
};
export const getActiveShiftCode = () => activeShiftCode;
export const getSyncQueue = () => queue.get();

// ── FLUSH QUEUED WRITES ───────────────────────────────────────
export const flushQueue = async () => {
  if (syncInProgress || !isFirebaseConfigured() || !activeShiftCode) return false;
  const q = queue.get();
  if (!q.length) return true;
  syncInProgress = true;
  try {
    for (const entry of q) {
      if (entry.type === 'save' && entry.patient) {
        await savePatient(activeShiftCode, entry.patient);
      } else if (entry.type === 'delete' && entry.patientId) {
        await removePatient(activeShiftCode, entry.patientId);
      } else if (entry.type === 'teams' && entry.teams) {
        await saveShiftMeta(activeShiftCode, entry.teams);
      }
    }
    queue.clear();
    syncInProgress = false;
    return true;
  } catch {
    syncInProgress = false;
    return false;
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { setTimeout(flushQueue, 1000); });
}

// ── MAIN STORAGE API ──────────────────────────────────────────
export const stor = {

  // Save all patients (used for bulk ops like archive, midnight clear)
  saveAll: async (patients, teams) => {
    cachedTeams = teams;
    local.set('patients', patients);
    local.set('teams', teams);
    local.set('meta', { saved: new Date().toISOString(), count: patients.length });

    if (!isFirebaseConfigured() || !activeShiftCode) return;

    if (!navigator.onLine) {
      // Queue each patient individually so we can flush per-patient later
      for (const p of patients) queue.push({ type: 'save', patientId: p.id, patient: p });
      throw new Error('offline');
    }
    try {
      await savePatientsBatch(activeShiftCode, patients);
      queue.clear();
    } catch(e) {
      for (const p of patients) queue.push({ type: 'save', patientId: p.id, patient: p });
      throw e;
    }
  },

  // Save a single patient (primary write path for all edits)
  saveOne: async (patient, teams) => {
    if (teams) cachedTeams = teams;

    // Update local cache
    const current = local.get('patients') || [];
    const updated = current.some(p => p.id === patient.id)
      ? current.map(p => p.id === patient.id ? patient : p)
      : [...current, patient];
    local.set('patients', updated);
    local.set('meta', { saved: new Date().toISOString(), count: updated.length });

    if (!isFirebaseConfigured() || !activeShiftCode) return;

    if (!navigator.onLine) {
      queue.push({ type: 'save', patientId: patient.id, patient });
      throw new Error('offline');
    }
    try {
      await savePatient(activeShiftCode, patient);
      queue.remove(patient.id);
    } catch(e) {
      queue.push({ type: 'save', patientId: patient.id, patient });
      throw e;
    }
  },

  // Delete a single patient
  deleteOne: async (patientId) => {
    const current = local.get('patients') || [];
    local.set('patients', current.filter(p => p.id !== patientId));

    if (!isFirebaseConfigured() || !activeShiftCode) return;

    if (!navigator.onLine) {
      queue.push({ type: 'delete', patientId });
      throw new Error('offline');
    }
    try {
      await removePatient(activeShiftCode, patientId);
      queue.remove(patientId);
    } catch(e) {
      queue.push({ type: 'delete', patientId });
      throw e;
    }
  },

  // Save teams (shift-level doc only)
  saveTeams: async (teams) => {
    cachedTeams = teams;
    local.set('teams', teams);

    if (!isFirebaseConfigured() || !activeShiftCode) return;

    if (!navigator.onLine) {
      queue.push({ type: 'teams', teams });
      return;
    }
    try {
      await saveShiftMeta(activeShiftCode, teams);
    } catch {
      queue.push({ type: 'teams', teams });
    }
  },

  loadAll: async () => {
    if (isFirebaseConfigured() && activeShiftCode) {
      try {
        const [meta, patients] = await Promise.all([
          loadShiftMeta(activeShiftCode),
          loadPatients(activeShiftCode),
        ]);
        const result = {
          patients: patients || local.get('patients') || [],
          teams: meta?.teams || local.get('teams') || [],
          meta: meta?.meta || local.get('meta') || {},
        };
        local.set('patients', result.patients);
        local.set('teams', result.teams);
        return result;
      } catch {
        return { patients: local.get('patients'), teams: local.get('teams'), meta: local.get('meta') };
      }
    }
    return { patients: local.get('patients'), teams: local.get('teams'), meta: local.get('meta') };
  },

  hasQueuedWrites: () => queue.get().length > 0,
};

export const SK = 'patients';
export const MK = 'meta';
export const TK = 'teams';
